/*!

=========================================================
* Argon Dashboard React - v1.2.3
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
/*eslint-disable*/
import DynamicSearchBox from 'components/Misc/DynamicSearchBox';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Link,
  NavLink as NavLinkRRD,
  useLocation,
  useNavigate,
} from 'react-router-dom';
// nodejs library to set properties for components
import { PropTypes } from 'prop-types';
import { useMediaQuery } from 'react-responsive';
import { getSessionData } from '../../EMR_Integration/react_webInjector.ts';
import NewConversationButton from '../Misc/NewConversationButton';

// reactstrap components
import {
  Col,
  Collapse,
  Container,
  Nav,
  Navbar,
  NavbarBrand,
  NavItem,
  NavLink,
  Row,
} from 'reactstrap';
import { removeSessionKey } from '../../api/Common';
import { GlobalContext } from '../../api/GlobalContext';
import { fetchUserDetails } from '../Misc/apiUtils';
import FloatingBugButton from '../Misc/FloatingBugButton';
import APP_VERSION from '../../api/AppVersion';
import { goToEmrOnByDefault } from '../../constants/goToEmr';
import { NAV_ICONS, NavIcon } from './navIcons';
import './Sidebar.css';
var ps;
let newUpdate = '55';
const isCX = window.location.protocol === 'chrome-extension:';
const PLAYGROUND_URL = 'https://playground.comprehendpt.ai';

const Sidebar = (props) => {
  const { apiClient } = useContext(GlobalContext);
  const bugButtonRef = useRef(null);
  const navigate = useNavigate();
  const [collapseOpen, setCollapseOpen] = useState();
  const [userDetails, setUserDetails] = useState({});
  const isSmallScreen = useMediaQuery({ query: '(max-width: 768px)' });
  const [showSearch, setShowSearch] = useState(false);

  // Reset search view when screen size changes to large
  useEffect(() => {
    if (!isSmallScreen) {
      setShowSearch(false);
    }
  }, [isSmallScreen]);
  // verifies if routeName is the one active (in browser input)
  const activeRoute = (routeName) => {
    return props.location.pathname.indexOf(routeName) > -1 ? 'active' : '';
  };
  // toggles collapse between opened and closed (true/false)
  const toggleCollapse = () => {
    setCollapseOpen((data) => !data);
  };
  // closes the collapse
  const closeCollapse = () => {
    setCollapseOpen(false);
  };
  // creates the links that appear in the left menu / Sidebar
  const createLinks = (routesToRender) => {
    return routesToRender.map((prop) => {
      return (
        <NavItem key={prop.layout + prop.path}>
          <NavLink
            to={prop.layout + prop.path}
            tag={NavLinkRRD}
            onClick={closeCollapse}
          >
            <div id={'nav-' + prop.name.split(' ').join('')} className="d-flex align-items-center">
              {NAV_ICONS[prop.path] ? (
                <NavIcon name={prop.path} />
              ) : (
                <i className={prop.icon} />
              )}
              <span className={NAV_ICONS[prop.path] ? '' : 'ml-2'}>{prop.name}</span>
            </div>
          </NavLink>
        </NavItem>
      );
    });
  };

  const platform = sessionStorage.getItem('platform');

  const { bgColor, routes, logo } = props;
  const managesBilling =
    userDetails?.role === 'Admin' || userDetails?.role === 'Coordinator';
  const filteredRoutes = useMemo(() => {
    return routes
      // Routes that must exist but never appear as a sidebar entry.
      .filter((route) => route.sidebar !== false)
      .filter(
        (route) =>
          route.show ||
          (route.path === '/emr' &&
            (userDetails?.features?.['go_to_emr'] === true ||
              goToEmrOnByDefault(userDetails?.EMR)) &&
            (platform === 'ios' || platform === 'android')) || // Platform check, Native Only
          // Admins and coordinators get it in the Admin group below
          (route.path === '/account/billing' &&
            !managesBilling &&
            platform !== 'ios' &&
            platform !== 'android') ||
          (route.path === '/reseller/portal' &&
            (userDetails?.isReseller || userDetails?.isDev)) ||
          (route.path === '/admin/dashboard' && userDetails?.isDev) ||
          (route.path === '/template/library' && userDetails?.isDev),
      )
      .filter(
        (route) =>
          // Hide Create Template if user has picked an EMR (excluding 'Other' and 'None')
          !(
            route.path === '/templates/choose' &&
            userDetails?.EMR &&
            userDetails.EMR !== '' &&
            userDetails.EMR !== 'Other' &&
            userDetails.EMR !== 'None'
          ),
      );
  }, [routes, userDetails]);
  // Admin group (COM-471): what used to be the Admin Settings tabs, plus billing
  const adminRoutes = useMemo(() => {
    const role = userDetails?.role;
    if (!['Admin', 'Coordinator', 'mini_admin'].includes(role)) return [];
    return routes.filter((route) => {
      if (!route.adminSection) return false;
      // Per the COM-471 design coordinators get Team + billing only
      if (route.path === '/admin/insights') return role !== 'Coordinator';
      if (route.path === '/admin/guidelines') return role === 'Admin';
      if (route.path === '/account/billing') {
        return managesBilling && platform !== 'ios' && platform !== 'android';
      }
      return true;
    });
  }, [routes, userDetails]);
  let navbarBrandProps;
  if (logo && logo.innerLink) {
    navbarBrandProps = {
      to: logo.innerLink,
      tag: Link,
    };
  } else if (logo && logo.outterLink) {
    navbarBrandProps = {
      href: logo.outterLink,
      target: '_blank',
    };
  }

  const location = useLocation();
  // Collapsed by default so the Admin group stays visible; open on its own pages
  const [resourcesToggled, setResourcesToggled] = useState(null);
  const resourcesOpen =
    resourcesToggled ??
    ['/User/integrations', '/User/releases'].includes(location.pathname);
  // The old slide-deck tutorial is gone; "Getting started" opens the Playground.
  // Upstream requested a different feedback URL for URPT users - Condition this based on the email domain
  const [feedbackUrl, setFeedbackUrl] = useState(
    'https://share.hsforms.com/2UTQzeApHSh2XiGb9htcXqQu73ak',
  );
  const URPT_FEEDBACK_URL =
    'https://thesource.urpt.com/tracker/share/MzU1MDU6OjU5MTk6OjExNTU3?embed_from_post=false';

  useEffect(() => {
    // Fetch user details when the component mounts
    fetchUserDetails(apiClient)
      .then((data) => setUserDetails(data))
      .catch((error) => console.error('Error fetching user details:', error));
  }, []);

  useEffect(() => {
    let emailCheckInterval;
    let attempts = 0;

    const pollEmail = () => {
      if (apiClient.email) {
        const emailDomain = apiClient.email.split('@')[1];
        if (
          ['urpt.com', 'benchmarkpt.com', 'drayerpt.com'].includes(emailDomain)
        ) {
          setFeedbackUrl(URPT_FEEDBACK_URL); // Replace with the correct URL
        }
        clearInterval(emailCheckInterval);
      } else if (attempts >= 5) {
        clearInterval(emailCheckInterval);
      } else {
        attempts += 1;
      }
    };

    emailCheckInterval = setInterval(pollEmail, 2000);

    return () => clearInterval(emailCheckInterval);
  }, [apiClient]);

  return (
    <Navbar
      className="navbar-vertical fixed-left navbar-light bg-white"
      expand="md"
      id="sidenav-main"
    >
      <FloatingBugButton ref={bugButtonRef} />
      <Container
        fluid
        style={{
          padding: '0',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          flexWrap: 'nowrap',
        }}
      >
        {/* Toggler */}
        <button
          className="navbar-toggler"
          id="navbar-menu-button"
          type="button"
          onClick={toggleCollapse}
          style={{
            margin: '0',
            marginLeft: '0',
            marginRight: isSmallScreen ? '24px' : '0',
            padding: '4px',
            position: 'relative',
            zIndex: 2,
          }}
        >
          <span className="navbar-toggler-icon" />
        </button>
        {!showSearch || !isSmallScreen ? (
          <>
            <NavbarBrand
              className="pt-0 pl-0"
              {...navbarBrandProps}
              style={{
                position: 'absolute',
                left: '50%',
                transform: isSmallScreen
                  ? 'translateX(calc(-50% - 76px))'
                  : 'translateX(-50%)',
                margin: 0,
                zIndex: 1,
              }}
            >
              <img
                alt={logo.imgAlt}
                className="navbar-brand-img"
                src={logo.imgSrc}
                style={{
                  width: isSmallScreen
                    ? '96px'
                    : isSmallScreen
                      ? '110px'
                      : '300px',
                  maxWidth: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
            </NavbarBrand>
            <Nav
              className="align-items-center d-md-none"
              style={{ marginLeft: 'auto', gap: '8px' }}
            >
              <i
                className="fas fa-search cursor-pointer"
                style={{
                  fontSize: '1rem',
                  color: '#2dce89',
                  marginLeft: '5px',
                  marginRight: '5px',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  lineHeight: 1,
                  verticalAlign: 'middle',
                  position: 'relative',
                  top: '1px',
                }}
                onClick={() => setShowSearch(true)}
              />
              <NewConversationButton id="new-conversation-button-sidebar" />
            </Nav>
          </>
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              margin: '0',
              padding: '0',
            }}
          >
            <div
              style={{
                flex: 1,
                minWidth: '0',
                position: 'relative',
                marginLeft: '0',
              }}
            >
              <DynamicSearchBox styleType="mobile" />
            </div>
            <NewConversationButton id="new-conversation-button-sidebar" />
          </div>
        )}
        {/* Collapse */}
        <Collapse navbar isOpen={collapseOpen}>
          {/* Collapse header */}
          <div className="navbar-collapse-header d-md-none">
            <Row>
              {logo ? (
                <Col className="collapse-brand" xs="6">
                  {logo.innerLink ? (
                    <Link to={logo.innerLink}>
                      <img alt={logo.imgAlt} src={logo.imgSrc} />
                    </Link>
                  ) : (
                    <a href={logo.outterLink}>
                      <img alt={logo.imgAlt} src={logo.imgSrc} />
                    </a>
                  )}
                </Col>
              ) : null}
              <Col className="collapse-close" xs="6">
                <button
                  className="navbar-toggler"
                  id="navbar-menu-close"
                  type="button"
                  onClick={toggleCollapse}
                >
                  <span />
                  <span />
                </button>
              </Col>
            </Row>
          </div>
          {/* Form */}
          {/* <Form className="mt-4 mb-3 d-md-none">
						<InputGroup className="input-group-rounded input-group-merge">
							<InputGroupAddon addonType="prepend">
								<InputGroupText>
									<span className="fa fa-search" />
								</InputGroupText>
							</InputGroupAddon>
							<Input
								placeholder="Search"
								type="search"
								aria-label="Search"
								className="form-control-rounded form-control-prepended"
							/>
						</InputGroup>
					</Form> */}
          {/* <DynamicSearchBox /> */}
          {/* Navigation */}
          <Nav navbar>
            {createLinks(filteredRoutes)}
            {/* Review Us option - Chrome Extension only */}
            {isCX && (
              <NavItem>
                <NavLink
                  onClick={() => {
                    window.dispatchEvent(new CustomEvent('show-review-prompt'));
                    closeCollapse();
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <div>
                    <i className="fa fa-star text-yellow" />
                    <span className="ml-2">Review Us</span>
                  </div>
                </NavLink>
              </NavItem>
            )}
          </Nav>
          {adminRoutes.length > 0 && (
            <>
              <hr className="my-3" />
              <h6 className="navbar-heading text-muted">Admin</h6>
              <Nav navbar>{createLinks(adminRoutes)}</Nav>
            </>
          )}
          {/* Divider */}
          <hr className="my-3" />
          {/* Heading */}
          <button
            type="button"
            className="navbar-heading text-muted d-flex align-items-center justify-content-between w-100 border-0 bg-transparent p-0"
            aria-expanded={resourcesOpen}
            onClick={() => setResourcesToggled(!resourcesOpen)}
            style={{ cursor: 'pointer', minHeight: 32 }}
          >
            <h6 className="navbar-heading text-muted mb-0">Resources</h6>
            <i className={`fas fa-chevron-${resourcesOpen ? 'up' : 'down'}`} style={{ fontSize: '0.7rem' }} />
          </button>
          {/* Navigation */}
          <Nav navbar>
            {resourcesOpen && platform !== 'android' && platform !== 'ios' && (
              <div>
                <NavItem>
                  <NavLink
                    onClick={() => {
                      // The Playground: a safe copy of their EMR with a fake
                      // patient, same panel — where onboarding lives and where
                      // "mess around without touching anything real" lives after.
                      // Dev backend → dev Playground, so a QA build lands on the
                      // Playground that matches its API (and its onboarding data).
                      const base = apiClient?.backendEnv === 'dev'
                        ? 'https://playground.dev.comprehendpt.com'
                        : PLAYGROUND_URL;
                      const creds = apiClient?.sessionKey && apiClient?.sessionEmail
                        ? `&sessionKey=${encodeURIComponent(apiClient.sessionKey)}&sessionEmail=${encodeURIComponent(apiClient.sessionEmail)}`
                        : '';
                      // Their EMR's copy, not the Playground's default one —
                      // Advanced completion is recorded per EMR.
                      const emrSlug = String(apiClient?.EMR || '').toLowerCase().trim()
                        .replace(/\s*\(.*\)\s*$/, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
                      const emr = emrSlug ? `&emr=${encodeURIComponent(emrSlug)}` : '';
                      window.open(
                        `${base}/play?mode=onboard${creds}${emr}`,
                        '_blank',
                        'noopener,noreferrer',
                      );
                      closeCollapse();
                    }}
                    style={{ cursor: 'pointer' }}
                    id="help-center"
                  >
                    <NavIcon name="gettingStarted" />
                    Getting started
                  </NavLink>
                </NavItem>
              </div>
            )}
            {resourcesOpen && (
            <>
            <NavItem>
              <NavLink
                onClick={() => {
                  navigate('/User/integrations');
                  closeCollapse();
                }}
                style={{ cursor: 'pointer' }}
              >
                <NavIcon name="integrations" />
                Integrations &amp; Apps
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                onClick={() => {
                  navigate('/User/releases');
                  localStorage.setItem('new-update', newUpdate);
                  closeCollapse();
                }}
                style={{
                  cursor: 'pointer',
                }}
              >
                <NavIcon name="whatsNew" />

                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    position: 'relative',
                  }}
                >
                  What's New
                  {localStorage.getItem('new-update') !== newUpdate && (
                    <span className="animated-circle"></span>
                  )}
                </span>
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink href={feedbackUrl} target="_blank">
                <NavIcon name="feedback" />
                Feedback
              </NavLink>
            </NavItem>
            </>
            )}
            <NavItem>
              <NavLink
                onClick={() => {
                  bugButtonRef.current?.triggerBugButton();
                  closeCollapse();
                }}
                style={{ cursor: 'pointer' }}
              >
                <NavIcon name="support" />
                Support / Get Help
              </NavLink>
            </NavItem>
          </Nav>
          <hr className="my-3" />
          <Nav className="mb-md-3" navbar>
            <NavItem>
              <NavLink
                onClick={async () => {
                  removeSessionKey(apiClient);
                  await getSessionData(false).catch(() => {});
                  navigate('/auth/login');
                }}
                style={{ cursor: 'pointer' }}
              >
                <NavIcon name="logout" />
                Log out
              </NavLink>
            </NavItem>
          </Nav>
          {/* agentic_chat: what the page footer says, so the window's bottom edge stays free (the visit chat's input lives there) */}
          {(userDetails?.features?.agentic_chat === true || apiClient?.features?.agentic_chat) && (
            <p className="text-muted px-3 mt-3 mb-0" style={{ fontSize: '0.75rem', lineHeight: 1.4 }}>
              Comprehend can make mistakes, check important info.
              <br />©{new Date().getFullYear()}{' '}
              <a className="font-weight-bold" href="https://www.comprehendpt.com" rel="noopener noreferrer" target="_blank">
                Comprehend PT
              </a>{' '}
              · v{APP_VERSION}
            </p>
          )}
        </Collapse>
      </Container>
    </Navbar>
  );
};

Sidebar.defaultProps = {
  routes: [{}],
};

Sidebar.propTypes = {
  // links that will be displayed inside the component
  routes: PropTypes.arrayOf(PropTypes.object),
  logo: PropTypes.shape({
    // innerLink is for links that will direct the user within the app
    // it will be rendered as <Link to="...">...</Link> tag
    innerLink: PropTypes.string,
    // outterLink is for links that will direct the user outside the app
    // it will be rendered as simple <a href="...">...</a> tag
    outterLink: PropTypes.string,
    // the image src of the logo
    imgSrc: PropTypes.string.isRequired,
    // the alt for the img
    imgAlt: PropTypes.string.isRequired,
  }),
};

export default Sidebar;
